import api from "@/_helpers/api";
import { defineStore } from "pinia";
import type { Pagination, Selection } from "@/types";
import { axiosInstance } from "@/plugins/https";
import { useFiltersStore } from "@/_store/filters.module";
import { StoreModule } from "@/constants/store.ts";
import { getSelectionAsStringsArray } from "@/_helpers/selection";
import { parseOpenSearchQuery } from "@/_helpers/pagination";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { i18n } from "@/plugins/i18n";
import { useSelectorStore } from "@/_store/selector.module";
import { SubscriptionModule } from "@/constants/workplaces";
import type { AxiosResponse } from "axios";
import { firstPageQueryEntityActivityLogs } from "@/constants/general.ts";
import type { ActivityLogItem } from "@/_store/activity-logs.module.ts";
import type { EntityTicket } from "@/components/EntityTicketsList.vue";
import {
  ProtectedUsersAction,
  UsersMfaFiltersItem,
  UsersSuspendedFilterItem,
} from "@/constants/users.ts";
import type { UserLabel } from "@/_store/users-settings/user-labels.module.ts";
import type { OsType } from "@/constants/devices.ts";
import type { Service } from "@/constants/cloud-apps.ts";
import { isAccessRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export interface UserDetails {
  id: null | string;
  name: null | string;
  protectionGrantedTime: number;
  cloudServices: UserDataCloudService[];
  labels: UserLabel[];
  tickets: Array<EntityTicket>;
  activityLogs: Array<ActivityLogItem>;
  aliases: Array<string>;
  associatedDevices: Array<UserAssociatedDevice>;
  suspended: boolean;
  autoForwardingBlockingPolicySelected: boolean;
}

export interface UserDataCloudService {
  serviceName: Service;
  mfaEnabled?: boolean | null;
}

export interface UserBaseListItem {
  id: string;
  name: string;
  email: string;
  hasOpenTickets: boolean;
  vulnerabilities: UserVulnerability[];
  aliases: string[];
  suspended: boolean;
}

export interface UserListItem extends UserBaseListItem {
  labels: UserLabel[];
}

export interface UserAssociatedDevice {
  name: string;
  osDisplayName: string;
  osType: OsType;
  enrollmentCode: string;
  hostName: string;
}

export interface UserVulnerability {
  issue: string;
}

interface UsersState {
  loading: boolean;
  items: Array<UserListItem>;
  total: number;
  pagination: Pagination;
  usersLoading: boolean;
  search: null | string;
  userDetailsLoading: boolean;
  activityLogsLoading: boolean;
  userDetails: UserDetails;
}

const defaultUsersState: UsersState = {
  loading: false,
  items: [],
  total: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  usersLoading: true,
  search: null,
  userDetailsLoading: false,
  activityLogsLoading: false,
  userDetails: {
    id: null,
    name: null,
    protectionGrantedTime: 0,
    cloudServices: [],
    tickets: [],
    activityLogs: [],
    aliases: [],
    labels: [],
    associatedDevices: [],
    suspended: false,
    autoForwardingBlockingPolicySelected: false,
  },
};

export const useUsersStore = defineStore(StoreModule.USERS, {
  state: (): UsersState => ({ ...defaultUsersState }),
  actions: {
    async getUserDetails({ id }: UserListItem): Promise<void> {
      this.userDetailsLoading = true;

      try {
        const activityLogsAccessRestricted = isAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.ACTIVITY_LOGS
        );

        const ticketsAccessRestricted = (
          [
            SubscriptionModule.USER_DATA_GOVERNANCE,
            SubscriptionModule.CLOUD_SECURITY,
            SubscriptionModule.EMAIL_SECURITY,
          ] as const
        ).every((module) => isAccessRestricted(RolePermissionsScope.TICKETS, module));

        const userInfoRequest = api.getUserInfo(id);
        const userTicketsRequest = api.getUserTickets(id);
        const userActivityLogsRequest = {
          ...api.getUserActivityLogs(id),
          params: parseOpenSearchQuery(firstPageQueryEntityActivityLogs),
        };
        const userAssociatedDevicesRequest = api.getUserAssociatedDevices(id);

        const [
          userInfoResponse,
          userTicketsResponse,
          userActivityLogsResponse,
          userAssociatedDevicesResponse,
        ] = await Promise.all([
          axiosInstance.request(userInfoRequest),
          ticketsAccessRestricted
            ? Promise.resolve({ data: [] })
            : axiosInstance.request(userTicketsRequest),
          activityLogsAccessRestricted
            ? Promise.resolve({ data: { items: [] } })
            : axiosInstance.request(userActivityLogsRequest),
          axiosInstance.request(userAssociatedDevicesRequest),
        ]);

        this.userDetails = {
          ...userInfoResponse.data,
          tickets: [...userTicketsResponse.data],
          activityLogs: [...userActivityLogsResponse.data.items],
          associatedDevices: userAssociatedDevicesResponse.data,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.usersLoading = false;
        this.userDetailsLoading = false;
      }
    },
    async getItems(payload: { showSkeletonLoader: boolean } = { showSkeletonLoader: false }) {
      this.loading = true;
      this.usersLoading = payload?.showSkeletonLoader;

      const filtersStore = useFiltersStore();
      const {
        labels,
        search,
        widget,
        hasVulnerabilities,
        cloudServices,
        suspended,
        mfaFilters,
        autoForwardingFilters,
      } = filtersStore.filters.usersFilters;

      const { mfaFiltersRecord } = getMfaFilters(mfaFilters);

      const request = {
        ...api.getUsers({
          ...this.pagination,
          labels: labels.join(","),
          search,
          widget,
          hasVulnerabilities,
          cloudServices: cloudServices.join(","),
          suspended: suspended ? suspended === UsersSuspendedFilterItem.SUSPENDED : null,
          moreFilters: autoForwardingFilters.join(","),
          ...mfaFiltersRecord,
        }),
      };

      try {
        const { data } = await axiosInstance.request(request);
        this.items = data.items;
        this.total = data.total;
      } catch (error) {
        console.error(error);
      }

      this.loading = false;
      this.usersLoading = false;
    },
    async onPageChange(pagination: Pagination) {
      this.pagination = pagination;
      await this.getItems();
    },

    async undoActivityLog(id: string) {
      this.activityLogsLoading = true;

      try {
        const request = { ...api.activityLogsUndo(id) };

        await axiosInstance.request(request);

        const userActivityLogsRequest = {
          ...api.getUserActivityLogs(this.userDetails.id as string),
          params: parseOpenSearchQuery(firstPageQueryEntityActivityLogs),
        };

        const { data } = await axiosInstance.request(userActivityLogsRequest);

        this.userDetails.activityLogs = data.items;
      } catch (error) {
        console.error(error);
      }

      this.activityLogsLoading = false;
    },
    async applySingleUserAction(
      userId: string,
      {
        action,
        metadata,
      }: {
        action: ProtectedUsersAction;
        metadata?: Record<string, unknown>;
      }
    ) {
      const { deselectAllPages } = useSelectorStore();
      const request = {
        ...api.protectedUsersSingleAction(userId),
        data: {
          action,
          metadata,
        },
      };

      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(i18n.global.t(`snackbar.messages.users.${action}`));
        await this.getItems();
        deselectAllPages();
      } catch (e) {
        console.error(e);
      }
    },
    async applyBulkUserAction({
      action,
      metadata,
      selection,
    }: {
      action: ProtectedUsersAction;
      selection: Selection<UserListItem>;
      metadata?: Record<string, unknown>;
    }) {
      const filters = useFiltersStore();
      const { deselectAllPages } = useSelectorStore();
      const { search, labels, cloudServices } = filters.filters.usersFilters;
      const request = {
        ...api.protectedUsersBulkAction(),
        data: {
          action,
          selection: getSelectionAsStringsArray(selection, "id"),
          filter: { search, labels, cloudServices },
          metadata,
        },
      };

      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(i18n.global.t(`snackbar.messages.users.${action}`));
        await this.getItems();
        deselectAllPages();
      } catch (e) {
        console.error(e);
      }
    },
    getSingleUserActions(
      userId: string
    ): Promise<AxiosResponse<{ items: ProtectedUsersAction[] }>> {
      const request = {
        ...api.singleUserActionsList(userId),
      };

      return axiosInstance.request(request);
    },
    getUsersBulkActions(
      selection: Selection<UserListItem>
    ): Promise<AxiosResponse<{ items: ProtectedUsersAction[] }>> {
      const filters = useFiltersStore();
      const request = {
        ...api.usersListActions(),
        data: {
          filters: {
            ...filters.filters.usersFilters,
          },
          selection: {
            ...getSelectionAsStringsArray(selection, "id"),
          },
        },
      };

      return axiosInstance.request(request);
    },
  },
});

export function getMfaFilters(array: UsersMfaFiltersItem[]) {
  const mfaFiltersRecord: Record<string, string> = {};
  array.forEach((item) => {
    mfaFiltersRecord[item] = "true";
  });

  return { mfaFiltersRecord };
}
